import { useAtom } from "jotai";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Trash } from "@/assets/icons/ds";
import { AppearTransition } from "@/components/Interface/AppearTransition/AppearTransition";
import { Button } from "@/components/Interface/Button/Button";
import { Modal } from "@/components/Interface/Modal/Modal";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { LightboxWrapper } from "@/components/Layout/Payment/LightboxWrapper/LightboxWrapper";
import {
  paymentProviderIcons,
  paymentProviderLabels,
  PaymentProviderPicker,
} from "@/components/Layout/Payment/PaymentProviderPicker/PaymentProviderPicker";
import { BillingAccounts } from "@/components/Portal/Documents/BillingAccounts/BillingAccounts";
import { useAlert } from "@/hooks/useAlert";
import { useBillingAccountAccess } from "@/hooks/useBillingAccountAccess";
import { useHandleCheckoutError } from "@/hooks/useHandleCheckoutError";
import { useIsMobile } from "@/hooks/useIsMobile";
import { usePaymentMode } from "@/hooks/usePaymentMode";
import { useQueryParam } from "@/hooks/useQueryParam";
import {
  useCreateTransaction,
  useDeletePaymentMean,
  useGetCustomer,
} from "@/services/api";
import {
  DrCreditCardCardType,
  DrPaymentMeanReasonsItem,
} from "@/services/model";
import { AlertTypes, userAccountStateAtom } from "@/utils/atoms";
import { isBusinessCustomer } from "@/utils/customerUtils";
import { NamespaceKey } from "@/utils/translationKey";

import { RequestInvoicePanel } from "./RequestInvoicePanel";

interface Props {
  /**
   * Bills tab shows the active payment method for reference only - no
   * change/add/delete. Managing it stays exclusive to the User account tab.
   */
  readOnly?: boolean;
}

/** The edit toggle cancels while editing, else adds or changes the method. */
const toggleButton = {
  cancel: {
    dataTestid: "payment-method-toggle-button",
    variant: "secondary",
    labelKey: "cancel",
  },
  add: {
    dataTestid: "add-payment-method-button",
    variant: "primary",
    labelKey: "addPaymentMethod",
  },
  change: {
    dataTestid: "payment-method-toggle-button",
    variant: "primary",
    labelKey: "change",
  },
} as const;

/** Checked in this order: the first reason the API sends picks the alert. */
const deleteErrorKey: Partial<
  Record<
    DrPaymentMeanReasonsItem,
    NamespaceKey<"portal", "user-account.paymentMethods">
  >
> = {
  [DrPaymentMeanReasonsItem.OPEN_BILL]: "errorAlerts.openBill",
  [DrPaymentMeanReasonsItem.PAY_AS_YOU_GO_ON]: "errorAlerts.payAsYouGo",
  [DrPaymentMeanReasonsItem.RATED_SERVICES_SETTINGS_ON]:
    "errorAlerts.serviceSettingsOn",
  [DrPaymentMeanReasonsItem.OUTSTANDING_RATED_SERVICES_CHARGES]:
    "errorAlerts.outstandingCharges",
};

export const PaymentMethods: FC<Props> = ({ readOnly = false }) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "user-account.paymentMethods",
  });
  const { t: tCommon } = useTranslation("portal", {
    keyPrefix: "common.buttons",
  });
  const [paymentProvider, setPaymentProvider] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userAccountState, setUserAccountState] = useAtom(userAccountStateAtom);
  const { data: customer } = useGetCustomer();
  const { editingPayment } = userAccountState;

  const setAddingOrChanging = (isEditing: boolean) => {
    setUserAccountState((prev) => ({
      ...prev,
      editingPayment: isEditing,
    }));
  };
  const showAlert = useAlert();

  const [paymentStatus, setPaymentStatus] = useQueryParam("payment");
  const isSuccess = paymentStatus === "success";
  useEffect(() => {
    if (isSuccess) {
      showAlert({
        type: AlertTypes.success,
        text: t("changeSuccessAlert"),
      });
      setPaymentStatus("", { replace: true });
      setUserAccountState((prev) => ({ ...prev, editingPayment: false }));
    }
  }, [setUserAccountState, setPaymentStatus, t, showAlert, isSuccess]);

  useHandleCheckoutError(true);

  const {
    isInvoice,
    isCreditCard,
    maskedCardNumberString,
    cardType,
    noPaymentMethod,
    canDeleteCreditCard,
    refetch,
    isLoading: paymentMeansAreLoading,
  } = usePaymentMode();
  const { hasBillingAccounts, isLoading: isLoadingBillingAccountAccess } =
    useBillingAccountAccess();

  // Handle errors for deleting payment mean. `reasons` is the raw error body
  // when the request fails, so `includes` also matches inside that string.
  const handleErrors = (
    reasons: string | DrPaymentMeanReasonsItem[] | undefined,
  ) => {
    const reason = (
      Object.keys(deleteErrorKey) as DrPaymentMeanReasonsItem[]
    ).find((key) => reasons?.includes(key));

    showAlert({
      type: AlertTypes.error,
      text: t(
        (reason && deleteErrorKey[reason]) || "errorAlerts.generalErrorAlert",
      ),
    });
  };

  const { mutate: deletePaymentMean } = useDeletePaymentMean({
    mutation: {
      onSuccess: (data) => {
        if (canDeleteCreditCard === true) {
          showAlert({
            type: AlertTypes.success,
            text: t("deleteSuccessAlert"),
          });
          refetch().then(() => {
            setAddingOrChanging(false);
            setIsModalOpen(false);
          });
        }
        if (data.canDeleteCreditCard === false) {
          handleErrors(data.reasons);
        }
      },
      onError: (error) => handleErrors(`${error.response?.data}`),
    },
  });

  const {
    data: datatransRequestDocument,
    mutate: createTransaction,
    reset: resetTransaction,
  } = useCreateTransaction();

  const handleAddingOrChanging = (s: DrCreditCardCardType | "") => {
    setPaymentProvider(s);
    resetTransaction();
    createTransaction({ params: undefined }, {});
  };

  // `editingPayment` lives in a global atom that isn't reset on navigation,
  // so a stale in-progress edit from the User account tab must not leak
  // edit-mode UI into the read-only Bills tab. Invoice customers also cannot
  // switch to another method, so a leftover flag must not open the picker.
  const isEditingPaymentMethod = editingPayment && !readOnly && !isInvoice;

  const isEditingAsBusinessCustomer =
    isEditingPaymentMethod && customer && isBusinessCustomer(customer);

  const isMobile = useIsMobile();
  // Match Login/Security and the bills list: on mobile the edit card is a
  // standalone screen, so the sibling card must not sit above the pinned
  // title bar.
  const isStandaloneMobileEdit = isMobile && isEditingPaymentMethod;
  const invoiceRequestHint = isEditingAsBusinessCustomer ? (
    <RequestInvoicePanel />
  ) : undefined;

  const toggleMode = editingPayment
    ? "cancel"
    : noPaymentMethod
      ? "add"
      : "change";
  const toggle = toggleButton[toggleMode];

  const actions = !readOnly && !isInvoice && (
    <>
      <Button
        dataTestid={toggle.dataTestid}
        variant={toggle.variant}
        size="sm"
        onClick={() => {
          if (editingPayment) {
            setPaymentProvider("");
            resetTransaction();
            setIsLoading(false);
          }
          setAddingOrChanging(!editingPayment);
        }}
      >
        {t(toggle.labelKey)}
      </Button>
      {editingPayment && !noPaymentMethod && (
        <Button
          dataTestid="save-payment-method-button"
          variant="primary"
          size="sm"
          disabled={!paymentProvider}
          onClick={() => {
            if (paymentProvider) {
              handleAddingOrChanging(paymentProvider as DrCreditCardCardType);
              setIsLoading(true);
            }
          }}
        >
          {tCommon("save")}
        </Button>
      )}
    </>
  );

  const mainCard = (
    <CardModule
      title={t("title")}
      // The default 428px cap left this narrower than its 50%-width column
      // on wider viewports - `none` lets TwoColumnLayout's grid size it.
      stickyHeader={isEditingPaymentMethod}
      subtitle={toggleMode === "add" ? t("noPaymentMethod") : undefined}
      actions={actions}
    >
      {isLoading && <LoadingSpinner />}
      {isModalOpen && (
        <Modal
          className="max-h-[500px]"
          variant="dark"
          title={t("deletePaymentmean")}
          onClose={() => setIsModalOpen(false)}
        >
          {t("confirmDelete")}
          <div className="mt-6 flex justify-end">
            <Button
              dataTestid="delete-button-modal"
              variant="primary"
              inverted
              onClick={() => {
                deletePaymentMean();
              }}
            >
              {t("deletePaymentmean")}
            </Button>
          </div>
        </Modal>
      )}
      {isInvoice && (
        <div
          data-testid="payment-method-invoice"
          className="rounded-lg bg-surface-tertiary px-4 py-3 text-base font-semibold text-foreground-primary"
        >
          {t("invoice")}
        </div>
      )}
      {isCreditCard && cardType && (
        <>
          <div className="flex items-center gap-3 rounded bg-surface-quaternary p-4">
            {paymentProviderIcons[cardType]}
            <div className="flex flex-1 items-center justify-between gap-3">
              <div>
                <div className="text-body-sm font-semibold leading-none text-foreground-primary">
                  {paymentProviderLabels[cardType]}
                </div>
                <div className="text-body-sm leading-none text-foreground-primary">
                  {maskedCardNumberString}
                </div>
              </div>
              {isEditingPaymentMethod && (
                <Button
                  dataTestid="delete-button"
                  ariaLabel={t("delete")}
                  variant="secondary"
                  size="sm"
                  icon={<Trash />}
                  onClick={() => setIsModalOpen(true)}
                />
              )}
            </div>
          </div>
          {isEditingPaymentMethod && (
            <hr className="h-px border-0 bg-border-tertiary" />
          )}
        </>
      )}
      {isEditingPaymentMethod && (
        <>
          <PaymentProviderPicker
            ariaLabel={t("title")}
            iconPlacement="inline"
            selected={paymentProvider}
            onChange={(s: DrCreditCardCardType) => {
              if (noPaymentMethod) {
                handleAddingOrChanging(s);
                setIsLoading(true);
              } else {
                setPaymentProvider(s);
              }
            }}
          />
          <LightboxWrapper
            paymentProvider={paymentProvider}
            transactionId={datatransRequestDocument?.refNo}
            sign={datatransRequestDocument?.sign}
            enabled={
              !!(
                datatransRequestDocument?.refNo &&
                datatransRequestDocument?.sign
              )
            }
          />
        </>
      )}
      {isStandaloneMobileEdit && invoiceRequestHint}
    </CardModule>
  );

  return (
    <section id="payment-methods">
      {paymentMeansAreLoading || isLoadingBillingAccountAccess ? (
        <LoadingSpinner />
      ) : (
        <AppearTransition>
          {hasBillingAccounts ? (
            <BillingAccounts />
          ) : (
            <TwoColumnLayout
              rows={[
                {
                  left: mainCard,
                  right: isStandaloneMobileEdit
                    ? undefined
                    : invoiceRequestHint,
                },
              ]}
            />
          )}
        </AppearTransition>
      )}
    </section>
  );
};
